import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import { AdminConfigService } from '../services/AdminConfigService';
import { useLoadingContext } from '../contexts/LoadingContext';

type AdminConfigLoaderParams = {
  getUserToken: () => string | undefined | Promise<string | undefined>;
  setAdminConfig: (value: any) => void;
  setAdminConfigError: (value: string | null) => void;
  adminConfigLoadedRef: MutableRefObject<boolean>;
  adminConfigLoadPromiseRef: MutableRefObject<Promise<void> | null>;
  adminConfigDirtyRef: MutableRefObject<boolean>;
  setDocumentAssistantDrafts: (value: any[]) => void;
  markAdminConfigDirty: (value: boolean) => void;
  loadProfileFiles: (token: string) => Promise<void>;
};

export const useAdminConfigLoader = (params: AdminConfigLoaderParams) => {
  const { beginLoadingKey } = useLoadingContext();
  const loadAdminConfig = useCallback(async (accessToken: string, options?: { force?: boolean }) => {
    if (!accessToken) return;
    const force = options?.force ?? false;
    if (!force && params.adminConfigLoadedRef.current) return;
    if (!force && params.adminConfigLoadPromiseRef.current) return params.adminConfigLoadPromiseRef.current;

    params.setAdminConfigError(null);
    const stopLoading = beginLoadingKey('adminConfigLoad');

    const promise = (async () => {
      try {
        const cfg = await AdminConfigService.loadAdminViewModel(accessToken);
        params.setAdminConfig(cfg ?? null);

        if (force || !params.adminConfigDirtyRef.current) {
          params.setDocumentAssistantDrafts(cfg?.documentAssistants ?? []);
          params.markAdminConfigDirty(false);
        }

        await params.loadProfileFiles(accessToken);
        params.adminConfigLoadedRef.current = true;
        stopLoading();
      } catch (err: any) {
        console.error('LoadAdminConfig fehlgeschlagen', err);
        params.setAdminConfigError(err?.message ?? 'Unbekannter Fehler');
        stopLoading();
      } finally {
        params.adminConfigLoadPromiseRef.current = null;
      }
    })();

    params.adminConfigLoadPromiseRef.current = promise;
    return promise;
  }, [params, beginLoadingKey]);

  const handleReloadFrontendConfig = useCallback(async () => {
    const token = await params.getUserToken();
    await loadAdminConfig(token as string, { force: true });
  }, [params, loadAdminConfig]);

  return { loadAdminConfig, handleReloadFrontendConfig };
};
